import * as THREE from 'three';
import { LANE_WIDTH, RUN } from '../game/tuning';
import { buildBike, type BikeRig } from '../assets/bikeFactory';
import { TweenManager, easeOutCubic } from '../systems/effects/Tween';

/**
 * The hover bike. Owns lane state and the visual rig; the collision footprint
 * is a fixed-size box around (laneX, z=0) handled by CollisionSystem.
 */
export class PlayerBike {
  readonly group = new THREE.Group();
  /** Current lane index: -1, 0, 1. */
  lane = 0;
  /** Visual x actually rendered (eased toward lane center). */
  visualX = 0;
  crashed = false;

  /** Collision half-extents (x, z). */
  static readonly HALF_WIDTH = 0.52;
  static readonly HALF_LENGTH = 1.05;

  private readonly rig: BikeRig;
  private bank = 0;
  private targetBank = 0;

  constructor(private readonly tweens: TweenManager) {
    this.rig = buildBike();
    this.group.add(this.rig.root);
  }

  tryChangeLane(direction: -1 | 1): boolean {
    const next = this.lane + direction;
    if (next < -1 || next > 1 || this.crashed) return false;
    this.lane = next as -1 | 0 | 1;
    const startX = this.visualX;
    const endX = this.lane * LANE_WIDTH;
    this.targetBank = direction * 0.42;
    this.tweens.tween(
      RUN.laneChangeDuration,
      (t) => {
        this.visualX = startX + (endX - startX) * t;
      },
      easeOutCubic,
      () => {
        this.targetBank = 0;
      },
    );
    return true;
  }

  crash(): void {
    this.crashed = true;
    this.targetBank = 0;
  }

  reset(): void {
    this.crashed = false;
    this.lane = 0;
    this.visualX = 0;
    this.bank = 0;
    this.targetBank = 0;
    this.rig.root.rotation.set(0, 0, 0);
    this.group.position.set(0, 0, 0);
    // The crash tween tumbles the OUTER group; clear it or the bike stays
    // twisted after restart.
    this.group.rotation.set(0, 0, 0);
  }

  /**
   * @param delta gameplay delta (scaled by hitstop)
   * @param elapsed accumulated game time for ambient motion
   * @param speedRatio 0..1 of current speed vs max, drives lean and hover
   */
  update(delta: number, elapsed: number, speedRatio: number): void {
    this.group.position.x = this.visualX;
    // Hover bob, flattened as speed rises.
    const bobAmp = 0.05 * (1 - speedRatio * 0.6);
    this.group.position.y = 0.02 + Math.sin(elapsed * 7) * bobAmp;

    // Bank into lane changes, settle smoothly.
    const smoothing = 1 - Math.exp(-14 * delta);
    this.bank += (this.targetBank - this.bank) * smoothing;
    this.rig.root.rotation.z = -this.bank;
    // Slight nose-down pitch grows with speed: reads as descending at pace.
    this.rig.root.rotation.x = 0.045 + speedRatio * 0.05;

    this.rig.update(delta, elapsed, speedRatio);
    if (this.crashed) {
      // Tumble handled by Game via tweens; keep rig glow dimmed.
      this.rig.setThrottle(0);
    } else {
      this.rig.setThrottle(speedRatio);
    }
  }

  dispose(): void {
    this.rig.dispose();
  }
}
